import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { QuestionMark } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import ReferenceRow from './ReferenceRow';
import { TRANSLATION_PREFIX } from '../consts';
import { readColumnWidths, writeColumnWidths } from '../utils';
import css from '../page.css';

const SCROLL_THRESHOLD_PX = 200;

function ReferenceGrid({
    translate,
    rows,
    tableName,
    visibleColumns,
    dictionaries,
    dictionaryOptions,
    perRowDictionaries,
    isValueCurrencyEnabled,
    valueTooltip,
    errorsByKey,
    filters,
    rowLimit,
    disabled,
    deleteDisabled,
    onFilterChange,
    onLoadMore,
    onCellChange,
    onDateChange,
    onValueChange,
    onDeleteRow,
}) {
    const sentinelRef = useRef(null);
    const headerRefs = useRef({});
    const observedWidths = useRef({});
    // Which column the pointer is currently resizing, if any -- see the resize observer below.
    const resizingField = useRef(null);
    const tableRef = useRef(null);
    const theadRef = useRef(null);
    const tbodyRef = useRef(null);
    const [railMetrics, setRailMetrics] = useState({ headerHeight: 0, rowHeights: [] });
    // Seeded from storage so the saved layout is applied on the first paint, not after a reflow.
    const [columnWidths, setColumnWidths] = useState(() => readColumnWidths(tableName));

    useEffect(() => {
        // Cleared with the widths: another table's baseline would read as a user resize on the first
        // measurement here, persisting a natural width nobody dragged.
        observedWidths.current = {};
        setColumnWidths(readColumnWidths(tableName));
    }, [tableName]);

    const persistWidth = useCallback(
        (field, width) => {
            setColumnWidths(current => {
                if (current[field] === width) {
                    return current;
                }
                // Widths of columns not currently visible are kept: an admin toggling a column off and
                // back on should find it the size they left it.
                const next = { ...current, [field]: width };
                writeColumnWidths(tableName, next);
                return next;
            });
        },
        [tableName]
    );

    // Disarmed on release anywhere, including outside the header the drag started on, so a reflow that lands
    // after the pointer is up is never mistaken for a resize. The ResizeObserver delivers the final frame's
    // width asynchronously at end-of-frame, after this handler runs, so its `resizingField.current === field`
    // gate would already be false and the last width lost - persist it here from the settled offsetWidth
    // before disarming (persistWidth is a no-op when the observer already stored the same value).
    useEffect(() => {
        const disarm = () => {
            const field = resizingField.current;
            if (field) {
                const element = headerRefs.current[field];
                if (element && element.offsetWidth) {
                    observedWidths.current[field] = element.offsetWidth;
                    persistWidth(field, element.offsetWidth);
                }
            }
            resizingField.current = null;
        };
        window.addEventListener('pointerup', disarm);
        window.addEventListener('pointercancel', disarm);
        return () => {
            window.removeEventListener('pointerup', disarm);
            window.removeEventListener('pointercancel', disarm);
        };
    }, [persistWidth]);

    // The native resize grip fires no event, so the header is watched. Only a change while the pointer is down
    // on that header counts as a resize: the observer also fires for font loads, content changes and plain
    // reflow, and persisting those would overwrite the width the user chose. The first measurement is the
    // column's natural width, so it only ever seeds the baseline.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(entries => {
            entries.forEach(entry => {
                const field = entry.target.dataset.columnField;
                // offsetWidth, not contentRect: the element is border-box and restored by setting `width`.
                const width = entry.target.offsetWidth;
                if (!field || !width) {
                    return;
                }
                const previous = observedWidths.current[field];
                observedWidths.current[field] = width;
                if (previous !== undefined && previous !== width && resizingField.current === field) {
                    persistWidth(field, width);
                }
            });
        });
        visibleColumns.forEach(column => {
            const element = headerRefs.current[column.field];
            if (element) {
                observer.observe(element);
            }
        });
        return () => observer.disconnect();
    }, [visibleColumns, persistWidth]);

    // The delete rail sits outside the scroller, so it has to mirror the table's row heights.
    useLayoutEffect(() => {
        const measure = () => {
            const headerHeight = theadRef.current ? theadRef.current.offsetHeight : 0;
            const rowHeights = tbodyRef.current
                ? [...tbodyRef.current.rows].map(tableRow => tableRow.offsetHeight)
                : [];
            setRailMetrics(current => {
                const unchanged =
                    current.headerHeight === headerHeight &&
                    current.rowHeights.length === rowHeights.length &&
                    current.rowHeights.every((height, index) => height === rowHeights[index]);
                return unchanged ? current : { headerHeight, rowHeights };
            });
        };
        measure();
        // A header wraps only once the web font loads, which can miss the observer entirely.
        const frame = requestAnimationFrame(measure);
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(measure);
        }
        if (typeof ResizeObserver === 'undefined') {
            return () => cancelAnimationFrame(frame);
        }
        // Observed on the table: a ResizeObserver on a table section does not reliably report reflows.
        const observer = new ResizeObserver(measure);
        if (tableRef.current) {
            observer.observe(tableRef.current);
        }
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
        // rows.length, not rows: the array is replaced on every keystroke, and re-running would read
        // offsetHeight for every rendered row before paint. Height changes reach the observer instead.
    }, [rows.length, visibleColumns, rowLimit, isValueCurrencyEnabled]);

    // Reveal more rows as the bottom of the list approaches the viewport (infinite scroll parity).
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || rows.length <= rowLimit) {
            return undefined;
        }
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    onLoadMore();
                }
            },
            { rootMargin: `${SCROLL_THRESHOLD_PX}px` }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [rows.length, rowLimit, onLoadMore]);

    const visibleRows = rows.slice(0, rowLimit);

    return (
        <div className={css.gridLayout}>
            {/* Outside the scrolling container by construction, so it cannot repaint with the scroll. */}
            <div className={css.actionRail}>
                <div className={css.railHeader} style={{ height: railMetrics.headerHeight || undefined }} />
                {visibleRows.map((row, index) => (
                    <div
                        key={row._key}
                        className={css.railCell}
                        style={{ height: railMetrics.rowHeights[index] || undefined }}
                    >
                        <button
                            type="button"
                            className={css.deleteButton}
                            title={translate(`${TRANSLATION_PREFIX}.DELETE_ROW`)}
                            aria-label={translate(`${TRANSLATION_PREFIX}.DELETE_ROW`)}
                            disabled={deleteDisabled}
                            onClick={() => onDeleteRow(row._key)}
                        >
                            <Icon name="trash" variant={IconVariants.LIGHT} size={IconSizes.SMALL} />
                        </button>
                    </div>
                ))}
            </div>
            <div className={css.gridContainer}>
                <table className={css.referenceTable} ref={tableRef}>
                    <thead ref={theadRef}>
                        <tr>
                            <th>
                                <span className={css.headerCell}>
                                    <Translate id={`${TRANSLATION_PREFIX}.RULE_START_DATE`} />
                                    <QuestionMark
                                        message={`${TRANSLATION_PREFIX}.LEAVE_BLANK_FOR_ALWAYS`}
                                        keepInViewport
                                    />
                                </span>
                            </th>
                            {visibleColumns.map(column => (
                                <th key={column.field} className={css.resizableHeaderCell}>
                                    {/* Only dimension headers resize: they hold the long values. The
                                    resizable block stays the sole child so its grip sits at the
                                    column edge; a tooltip goes beside the label like every other
                                    header, and `overflow: hidden` cannot clip the popup because
                                    tippy appends it to document.body. */}
                                    <div
                                        className={css.resizableHeader}
                                        // The grip is part of this element's own box, so a pointerdown on the
                                        // header is the only signal available that a drag may be starting.
                                        onPointerDown={() => {
                                            resizingField.current = column.field;
                                        }}
                                        data-column-field={column.field}
                                        ref={element => {
                                            headerRefs.current[column.field] = element;
                                        }}
                                        style={
                                            columnWidths[column.field]
                                                ? { width: `${columnWidths[column.field]}px` }
                                                : undefined
                                        }
                                    >
                                        <span className={css.headerCell}>
                                            <Translate id={column.label} />
                                            {column.tooltip && <QuestionMark message={column.tooltip} keepInViewport />}
                                        </span>
                                    </div>
                                </th>
                            ))}
                            <th>
                                <span className={css.headerCell}>
                                    <Translate id={`${TRANSLATION_PREFIX}.VALUE`} />
                                    {valueTooltip && (
                                        <QuestionMark message={valueTooltip} position="top-end" keepInViewport />
                                    )}
                                </span>
                            </th>
                            {isValueCurrencyEnabled && (
                                <th>
                                    <span className={css.headerCell}>
                                        <Translate id={`${TRANSLATION_PREFIX}.VALUE_CURRENCY`} />
                                    </span>
                                </th>
                            )}
                            <th className={css.spacerCell} aria-hidden="true" />
                        </tr>
                        <tr className={css.filterRow}>
                            <td />
                            {visibleColumns.map(column => (
                                <td key={column.field}>
                                    {/* The icon names the row as a filter; a bare box reads as another
                                    editable cell. Not focusable, so it never sits in the tab order. */}
                                    <div className={css.filterCell}>
                                        <Icon
                                            className={css.filterIcon}
                                            name="search"
                                            size={IconSizes.X_SMALL}
                                            variant={IconVariants.LIGHT}
                                            interactive={false}
                                        />
                                        <input
                                            className={css.filterInput}
                                            type="text"
                                            aria-label={`Filter ${column.field}`}
                                            value={filters[column.field] || ''}
                                            onChange={event => onFilterChange(column.field, event.target.value)}
                                        />
                                    </div>
                                </td>
                            ))}
                            <td />
                            {isValueCurrencyEnabled && <td />}
                            <td className={css.spacerCell} />
                        </tr>
                    </thead>
                    <tbody ref={tbodyRef}>
                        {visibleRows.map(row => (
                            <ReferenceRow
                                key={row._key}
                                row={row}
                                visibleColumns={visibleColumns}
                                dictionaries={dictionaries}
                                dictionaryOptions={dictionaryOptions}
                                perRowDictionaries={perRowDictionaries}
                                isValueCurrencyEnabled={isValueCurrencyEnabled}
                                errors={errorsByKey[row._key]}
                                disabled={disabled}
                                deleteDisabled={deleteDisabled}
                                onCellChange={(field, option, dictionary) =>
                                    onCellChange(row._key, field, option, dictionary)
                                }
                                onDateChange={startDate => onDateChange(row._key, startDate)}
                                onValueChange={value => onValueChange(row._key, value)}
                            />
                        ))}
                    </tbody>
                </table>
                <div ref={sentinelRef} className={css.scrollSentinel} />
            </div>
        </div>
    );
}

ReferenceGrid.propTypes = {
    translate: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    tableName: PropTypes.string,
    visibleColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    // Per-row dictionaries are keyed maps, so an entry is either shape.
    dictionaries: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    dictionaryOptions: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    perRowDictionaries: PropTypes.shape({
        loading: PropTypes.objectOf(PropTypes.bool).isRequired,
        onOpen: PropTypes.func.isRequired,
        onSearch: PropTypes.func.isRequired,
    }).isRequired,
    isValueCurrencyEnabled: PropTypes.bool,
    valueTooltip: PropTypes.string,
    errorsByKey: PropTypes.objectOf(PropTypes.object).isRequired,
    filters: PropTypes.objectOf(PropTypes.string).isRequired,
    rowLimit: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    deleteDisabled: PropTypes.bool,
    onFilterChange: PropTypes.func.isRequired,
    onLoadMore: PropTypes.func.isRequired,
    onCellChange: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    onDeleteRow: PropTypes.func.isRequired,
};

export default ReferenceGrid;
